import React from 'react';
import { Box, LinearProgress, Stack, Typography, alpha, colors } from '@mui/material';

import { currencyUtils } from 'skipify-types';

import { formatCurrency } from '../../../../util/formatters';
export type PartialPaymentSummaryAmounts = {
  paidAmountMinor: number;
  totalAmountMinor: number;
};

type Props = PartialPaymentSummaryAmounts & {
  currencyCode?: string;
  embedded?: boolean;
};

export const PartialPaymentSummaryPill = ({
  paidAmountMinor,
  totalAmountMinor,
  currencyCode,
  embedded = false,
}: Props): JSX.Element => {
  const paid = formatCurrency(currencyUtils.minorToMajor(paidAmountMinor), currencyCode);
  const percentPaid =
    totalAmountMinor > 0
      ? Math.min(100, Math.round((paidAmountMinor / totalAmountMinor) * 100))
      : 0;
  const remainingMinor = Math.max(0, totalAmountMinor - paidAmountMinor);
  const remaining = formatCurrency(currencyUtils.minorToMajor(remainingMinor), currencyCode);

  const content = (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'grey.800', lineHeight: 1.25 }}>
          Partial payment received
        </Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'grey.600', whiteSpace: 'nowrap' }}>
          {remaining} remaining
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={percentPaid}
        aria-label={`${remaining} of paylink total remaining`}
        sx={{
          height: 4,
          borderRadius: 3,
          backgroundColor: colors.grey[200],
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            backgroundColor: 'success.main',
          },
        }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: 'grey.600',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          Amount paid
        </Typography>
        <Typography
          sx={{
            fontSize: embedded ? 18 : 22,
            fontWeight: 700,
            color: 'grey.900',
            lineHeight: 1.1,
          }}
        >
          {paid}
        </Typography>
      </Stack>
    </Stack>
  );

  if (embedded) {
    return <Box data-testid="partial-payment-summary-pill">{content}</Box>;
  }

  return (
    <Box
      data-testid="partial-payment-summary-pill"
      sx={{
        width: '100%',
        maxWidth: 280,
        mt: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: '14px',
        backgroundColor: alpha(colors.common.white, 0.92),
        border: `1px solid ${colors.grey[300]}`,
        boxShadow: '0 2px 8px rgba(16, 24, 40, 0.06)',
      }}
    >
      {content}
    </Box>
  );
};
